import React, { useState, useEffect } from 'react';
import BlockIcon from '@mui/icons-material/Block';

import { LoadingImgIcon } from '../../assets/icons';

type ImageProps = {
  src?: string;
  alt?: string;
  className?: string;
  loadingSrc?: boolean;
  wrapperClassName?: string;
  imageClassName?: string;
  style?: React.CSSProperties;
  imageStyle?: React.CSSProperties;
  showLoadingIndicator?: boolean;
  showErrorIcon?: boolean;
  fallbackComponent?: React.ReactNode;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  onLoad?: () => void;
  onError?: () => void;
};

export const Image: React.FC<ImageProps> = ({
  loadingSrc = false,
  src,
  alt = 'Image',
  className,
  wrapperClassName,
  imageClassName,
  style,
  imageStyle,
  showLoadingIndicator = true,
  showErrorIcon = true,
  fallbackComponent,
  objectFit = 'cover',
  onLoad,
  onError,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!src) {
      setLoading(false);
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);

    const img = new window.Image();
    img.src = src;

    img.onload = () => {
      setLoading(false);
      setError(false);
      onLoad?.();
    };

    img.onerror = () => {
      setLoading(false);
      setError(true);
      onError?.();
    };
  }, [src, onLoad, onError]);

  const defaultWrapperClasses =
    'relative w-40 h-20 bg-gray-200 rounded-lg overflow-hidden flex justify-center items-center';
  const wrapperClasses = wrapperClassName || defaultWrapperClasses;
  const combinedWrapperClasses = `${wrapperClasses} ${className || ''}`.trim();

  const defaultImageClasses = `w-full h-full object-${objectFit}`;
  const finalImageClassName = imageClassName || defaultImageClasses;

  return (
    <div className={combinedWrapperClasses} style={style}>
      {(loading || loadingSrc) && showLoadingIndicator && (
        <div
          className="absolute inset-0 bg-gray-100 flex justify-center items-center transition-opacity duration-700"
          style={{ opacity: loading ? 0.9 : 0 }}
        >
          <div className="w-full h-full bg-gray-200 backdrop-blur-md filter blur-md" />
          <div className="absolute">
            <LoadingImgIcon />
          </div>
        </div>
      )}

      {!loading && !error && src && (
        <img
          src={src}
          alt={alt}
          className={finalImageClassName}
          style={imageStyle}
        />
      )}

      {error && (
        <>
          {fallbackComponent ? (
            fallbackComponent
          ) : showErrorIcon ? (
            <div className="absolute inset-0 flex flex-col justify-center items-center bg-gray-300 text-gray-600">
              <BlockIcon fontSize="large" />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
